/**
 * CreateOrganizationModal — Create a new root organization community.
 *
 * Entry point: "Create organization" button on the Lead → Communities page,
 * visible only to sysadmins (or users explicitly granted root-org creation
 * in a future permission tier).
 *
 * Creates a CommunityEntity with `entity_type = 'organization'` and no
 * parent — the canonical shape for a root org. The new community starts in
 * `visibility = 'private'` per DEV-890; the admin populates logo, info
 * card, MVV, etc. via the community settings modal after creation.
 *
 * Slug is auto-generated from the name client-side for preview; the server
 * has its own authoritative slugifier and final validation.
 *
 * DEV-977.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CloseButton from '../CloseButton/CloseButton';
import { createCommunityEntity, slugifyName } from '../../api/communityMetadata';
import styles from './CreateOrganizationModal.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new community's entity_guid after a successful create. */
  onCreated?: (entityGuid: string) => void;
}

const CreateOrganizationModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const { t } = useAppTranslation('community');
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const slugPreview = useMemo(() => slugifyName(name), [name]);
  const canSubmit = name.trim().length > 0 && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createCommunityEntity({
        name: name.trim(),
        entityType: 'organization',
        visibility: 'private',
        // Server auto-generates slug from name when omitted — pass our preview
        // so the URL matches what the admin sees in the modal.
        slug: slugPreview || undefined,
      });
      const newGuid = String(created.entityGuid);
      onCreated?.(newGuid);
      onClose();
      navigate(`/communities/${newGuid}`);
    } catch (e: any) {
      setError(e?.message ?? t('createOrg.errorGeneric'));
      setSubmitting(false);
    }
  }, [canSubmit, name, slugPreview, onCreated, onClose, navigate, t]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && canSubmit) handleSubmit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, canSubmit, handleSubmit, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('createOrg.title')}</h2>
          <CloseButton onClick={onClose} />
        </div>

        <div className={styles.body}>
          <p className={styles.subtitle}>{t('createOrg.subtitle')}</p>

          <div className={styles.field}>
            <label className={styles.label}>{t('createOrg.nameLabel')}</label>
            <input
              type="text"
              className={styles.input}
              value={name}
              autoFocus
              placeholder={t('createOrg.namePlaceholder')}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />
          </div>

          {slugPreview && (
            <div className={styles.slugPreview}>
              <span className={styles.slugLabel}>{t('createOrg.urlPreview')}</span>
              <code className={styles.slugCode}>/{slugPreview}</code>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            {t('createOrg.cancel')}
          </button>
          <button
            type="button"
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? t('createOrg.creating') : t('createOrg.create')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreateOrganizationModal;
